/**
 * Room Defaults page — admin-editable defaults for the Furniture Staffing
 * section. Lives at /furniture-projects/room-defaults so it's a peer of
 * Projects / Staffing rather than buried under /admin.
 *
 * For each room type there are TWO independently editable lists:
 *   1. Defaults — auto-populated when adding this room type to a project
 *      (split into Standard items and Essentials).
 *   2. Quick-add suggestions — chips that show in the Add-Room dialog so
 *      users can add common-but-not-default items in one click.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, X, Save, Sparkles, Sofa, RotateCcw, Lock, Link2 } from 'lucide-react';
import { getFurnitureRoomTemplates, updateFurnitureRoomTemplate, createFurnitureRoomTemplate, deleteFurnitureRoomTemplate } from '@/lib/api';
import { useAppAuth } from '@/contexts/AppAuthContext';
import EssentialLinksManager from '@/components/furniture/EssentialLinksManager';
import { ItemsCatalogPanel } from '@/components/furniture/ItemsCatalogPanel';
import { ItemPickerDialog } from '@/components/furniture/ItemPickerDialog';
import { EssentialPickerDialog } from '@/components/furniture/EssentialPickerDialog';

const cloneTpl = (t) => ({
  items: [...(t?.items || [])],
  essentials: [...(t?.essentials || [])],
  suggestions: {
    items: [...(t?.suggestions?.items || [])],
    essentials: [...(t?.suggestions?.essentials || [])],
  },
});

const sameTpl = (a, b) =>
  JSON.stringify(a.items) === JSON.stringify(b.items) &&
  JSON.stringify(a.essentials) === JSON.stringify(b.essentials) &&
  JSON.stringify(a.suggestions.items) === JSON.stringify(b.suggestions.items) &&
  JSON.stringify(a.suggestions.essentials) === JSON.stringify(b.suggestions.essentials);

export default function FurnitureRoomDefaultsPage() {
  const { isAdmin } = useAppAuth();
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeRoom, setActiveRoom] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(null);
  const [newRoomType, setNewRoomType] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [essentialPickerOpen, setEssentialPickerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getFurnitureRoomTemplates();
      setTemplates(data);
      const d = {};
      Object.entries(data).forEach(([rt, tpl]) => { d[rt] = cloneTpl(tpl); });
      setDraft(d);
      if (!activeRoom) {
        const sorted = Object.keys(data).sort();
        setActiveRoom(sorted[0]);
      }
    } catch {
      toast.error('Could not load room defaults');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const roomTypes = useMemo(() => Object.keys(templates).sort(), [templates]);
  const current = activeRoom ? draft[activeRoom] : null;
  const upstream = activeRoom ? templates[activeRoom] : null;
  const dirty = useMemo(
    () => current && upstream && !sameTpl(current, upstream),
    [current, upstream],
  );

  if (loading) {
    return <div className="p-6 text-sm text-[#5b6773]">Loading room defaults…</div>;
  }

  // ── List mutators ────────────────────────────────────────────────
  const setDefaultList = (key, updater) => setDraft((d) => ({
    ...d,
    [activeRoom]: { ...d[activeRoom], [key]: updater(d[activeRoom][key]) },
  }));
  const setSuggestionList = (key, updater) => setDraft((d) => ({
    ...d,
    [activeRoom]: {
      ...d[activeRoom],
      suggestions: { ...d[activeRoom].suggestions, [key]: updater(d[activeRoom].suggestions[key]) },
    },
  }));

  const removeDefault = (key, n) => setDefaultList(key, (xs) => xs.filter((x) => x !== n));
  const moveDefault = (from, to, n) => {
    setDefaultList(from, (xs) => xs.filter((x) => x !== n));
    setDefaultList(to, (xs) => xs.includes(n) ? xs : [...xs, n]);
  };
  const addDefault = (key, n) => setDefaultList(key, (xs) => xs.includes(n) ? xs : [...xs, n]);

  const removeSuggestion = (key, n) => setSuggestionList(key, (xs) => xs.filter((x) => x !== n));
  const moveSuggestion = (from, to, n) => {
    setSuggestionList(from, (xs) => xs.filter((x) => x !== n));
    setSuggestionList(to, (xs) => xs.includes(n) ? xs : [...xs, n]);
  };
  const addSuggestion = (key, n) => setSuggestionList(key, (xs) => xs.includes(n) ? xs : [...xs, n]);

  // Promoting a chip from suggestion → default (and vice versa)
  const promoteSuggestionToDefault = (key, n) => {
    setSuggestionList(key, (xs) => xs.filter((x) => x !== n));
    setDefaultList(key, (xs) => xs.includes(n) ? xs : [...xs, n]);
  };
  const demoteDefaultToSuggestion = (key, n) => {
    setDefaultList(key, (xs) => xs.filter((x) => x !== n));
    setSuggestionList(key, (xs) => xs.includes(n) ? xs : [...xs, n]);
  };

  // ── Save / Reset ─────────────────────────────────────────────────
  const save = async () => {
    if (!activeRoom || !current) return;
    setBusy(activeRoom);
    try {
      await updateFurnitureRoomTemplate(activeRoom, current);
      toast.success(`${activeRoom} defaults saved`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(null); }
  };

  const reset = () => {
    if (!activeRoom || !upstream) return;
    setDraft((d) => ({ ...d, [activeRoom]: cloneTpl(upstream) }));
  };

  // ── Room-type create / delete ───────────────────────────────────
  const createRoomType = async () => {
    const n = newRoomType.trim();
    if (!n) return;
    if (templates[n]) {
      toast.error('A room type with that name already exists');
      return;
    }
    try {
      await createFurnitureRoomTemplate(n);
      setNewRoomType('');
      await load();
      setActiveRoom(n);
      toast.success(`Added "${n}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create room type');
    }
  };
  const deleteRoomType = async (rt) => {
    if (!window.confirm(`Delete the "${rt}" room template? Existing project rooms aren't affected — they'll just keep their type label.`)) return;
    try {
      await deleteFurnitureRoomTemplate(rt);
      const remaining = Object.keys(templates).filter(x => x !== rt).sort();
      setActiveRoom(remaining[0] || null);
      await load();
      toast.success(`Deleted "${rt}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="room-defaults-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Sofa className="h-5 w-5 text-emerald-700" /> Room Defaults
        </h1>
        <p className="text-sm text-[#5b6773] mt-1">
          Curate what gets pre-populated when adding a room — and the default purchase
          links that ride along with each essential.
          {!isAdmin && (
            <span className="ml-1 inline-flex items-center gap-1 text-amber-700 font-medium">
              <Lock className="h-3 w-3" /> Read-only — admin access required to edit.
            </span>
          )}
        </p>
      </div>

      <Tabs defaultValue="rooms" className="space-y-5">
        <TabsList className="bg-card border p-1">
          <TabsTrigger value="rooms" className="text-xs gap-1.5" data-testid="rd-outer-tab-rooms">
            <Sofa className="h-3.5 w-3.5" /> Per-Room Defaults
          </TabsTrigger>
          <TabsTrigger value="ess-links" className="text-xs gap-1.5" data-testid="rd-outer-tab-ess-links">
            <Link2 className="h-3.5 w-3.5" /> Essential Links
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rooms" className="space-y-4">
          <p className="text-xs text-[#7c8794]">
            Changes apply to <strong>future</strong> rooms only — existing project rooms are unchanged.
          </p>
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={newRoomType}
                onChange={(e) => setNewRoomType(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createRoomType(); } }}
                placeholder="New room type (e.g. Office, Nursery)"
                className="h-8 text-sm w-64"
                data-testid="room-defaults-new-room-type"
              />
              <Button size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-800" onClick={createRoomType} disabled={!newRoomType.trim()} data-testid="room-defaults-add-room-type">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add room type
              </Button>
            </div>
          )}
          <Tabs value={activeRoom || ''} onValueChange={setActiveRoom}>
        <TabsList className="flex flex-wrap h-auto justify-start gap-1 bg-card p-1 border">
          {roomTypes.map((rt) => {
            const dirtyTab = draft[rt] && templates[rt] && !sameTpl(draft[rt], templates[rt]);
            return (
              <TabsTrigger key={rt} value={rt} className="text-xs" data-testid={`room-defaults-tab-${rt.toLowerCase().replace(/[\s/]/g, '-')}`}>
                {rt}
                {dirtyTab && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-amber-500"></span>}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {roomTypes.map((rt) => (
          <TabsContent key={rt} value={rt} className="space-y-6 mt-5">
            {activeRoom === rt && current && (
              <>
                {/* ── Defaults section ─────────────────────────── */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                      Defaults
                    </CardTitle>
                    <p className="text-xs text-[#5b6773]">
                      Auto-checked when {rt} is added to a project. Users can still uncheck individual items in the Add-Room dialog.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ChipBlock
                      label="Standard items" count={current.items.length}
                      items={current.items}
                      onRemove={(n) => removeDefault('items', n)}
                      moveActions={[
                        { label: '⇄ essentials', onClick: (n) => moveDefault('items', 'essentials', n) },
                        { label: '↓ to suggestion', onClick: (n) => demoteDefaultToSuggestion('items', n), disabled: !isAdmin },
                      ]}
                      chipClass="bg-emerald-50 text-emerald-800 border-emerald-200"
                      readOnly={!isAdmin}
                      testIdPrefix={`def-${rt}-item`}
                    />
                    <ChipBlock
                      label="Essentials" count={current.essentials.length}
                      items={current.essentials}
                      onRemove={(n) => removeDefault('essentials', n)}
                      moveActions={[
                        { label: '⇄ standard', onClick: (n) => moveDefault('essentials', 'items', n) },
                        { label: '↓ to suggestion', onClick: (n) => demoteDefaultToSuggestion('essentials', n), disabled: !isAdmin },
                      ]}
                      chipClass="bg-amber-50 text-amber-800 border-amber-200"
                      readOnly={!isAdmin}
                      testIdPrefix={`def-${rt}-ess`}
                    />
                    {isAdmin && (
                      <div className="space-y-2">
                        <CustomAddRow
                          onAdd={(n, asEss) => addDefault(asEss ? 'essentials' : 'items', n)}
                          testIdPrefix="def-add"
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline" size="sm" className="h-7 text-xs gap-1"
                            onClick={() => setPickerOpen(true)}
                            data-testid={`def-${rt}-pick-from-catalog`}
                          >
                            <Plus className="h-3 w-3" /> Pick from Items Catalog
                          </Button>
                          <Button
                            variant="outline" size="sm" className="h-7 text-xs gap-1 border-amber-300 text-amber-800 hover:bg-amber-50"
                            onClick={() => setEssentialPickerOpen(true)}
                            data-testid={`def-${rt}-pick-from-essentials`}
                          >
                            <Plus className="h-3 w-3" /> Pick from Essentials Library
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* ── Suggestion chips section ─────────────────── */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-violet-600" /> Quick-add suggestions
                    </CardTitle>
                    <p className="text-xs text-[#5b6773]">
                      Optional chips that appear in the Add-Room dialog. Users click to add — these aren't pre-checked.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ChipBlock
                      label="As standard items" count={current.suggestions.items.length}
                      items={current.suggestions.items}
                      onRemove={(n) => removeSuggestion('items', n)}
                      moveActions={[
                        { label: '⇄ essential', onClick: (n) => moveSuggestion('items', 'essentials', n) },
                        { label: '↑ make default', onClick: (n) => promoteSuggestionToDefault('items', n), disabled: !isAdmin },
                      ]}
                      chipClass="bg-violet-50 text-violet-800 border-violet-200"
                      readOnly={!isAdmin}
                      testIdPrefix={`sug-${rt}-item`}
                    />
                    <ChipBlock
                      label="As essentials" count={current.suggestions.essentials.length}
                      items={current.suggestions.essentials}
                      onRemove={(n) => removeSuggestion('essentials', n)}
                      moveActions={[
                        { label: '⇄ standard', onClick: (n) => moveSuggestion('essentials', 'items', n) },
                        { label: '↑ make default', onClick: (n) => promoteSuggestionToDefault('essentials', n), disabled: !isAdmin },
                      ]}
                      chipClass="bg-violet-50 text-violet-800 border-violet-200"
                      readOnly={!isAdmin}
                      testIdPrefix={`sug-${rt}-ess`}
                    />
                    {isAdmin && (
                      <CustomAddRow
                        onAdd={(n, asEss) => addSuggestion(asEss ? 'essentials' : 'items', n)}
                        testIdPrefix="sug-add"
                      />
                    )}
                  </CardContent>
                </Card>

                {isAdmin && (
                  <div className="flex items-center justify-between border-t pt-4">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={reset} disabled={!dirty} data-testid={`def-${rt}-reset`}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Discard changes
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => deleteRoomType(rt)}
                        data-testid={`def-${rt}-delete`}
                      >
                        <X className="h-3.5 w-3.5 mr-1.5" /> Delete room type
                      </Button>
                    </div>
                    <Button onClick={save} disabled={!dirty || busy === rt} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`def-${rt}-save`}>
                      <Save className="h-3.5 w-3.5 mr-1.5" />
                      {busy === rt ? 'Saving…' : `Save ${rt}`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        ))}
          </Tabs>
          {/* Items Catalog — embedded collapsible panel below the room
              tabs. Lazy-loaded on first expand. */}
          <ItemsCatalogPanel readOnly={!isAdmin} />
        </TabsContent>

        <TabsContent value="ess-links">
          <EssentialLinksManager readOnly={!isAdmin} />
        </TabsContent>
      </Tabs>

      <ItemPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title={`Add items to ${activeRoom || ''} defaults`}
        existingNames={current ? [...current.items, ...current.essentials] : []}
        onPick={async (name) => { addDefault('items', name); }}
      />

      <EssentialPickerDialog
        open={essentialPickerOpen}
        onOpenChange={setEssentialPickerOpen}
        onPick={async (name) => { addDefault('essentials', name); }}
      />
    </div>
  );
}

function ChipBlock({ label, count, items, onRemove, moveActions, chipClass, readOnly, testIdPrefix }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <p className="text-[11px] font-semibold text-[#5b6773] uppercase tracking-wider">{label}</p>
        <span className="text-[10px] text-[#9ca3af]">{count}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-[#9ca3af] italic">None{readOnly ? '.' : ' yet — use Add Custom below.'}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((name) => (
            <span key={name} className={`group inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${chipClass}`} data-testid={`${testIdPrefix}-chip-${name.replace(/\s+/g, '-').toLowerCase()}`}>
              {name}
              {!readOnly && moveActions && moveActions.map((a, i) => (
                <button key={i} type="button" onClick={() => a.onClick(name)} disabled={a.disabled} className="opacity-50 hover:opacity-100 text-[10px] disabled:opacity-30 disabled:hover:opacity-30" title={a.label}>
                  {a.label.split(' ')[0]}
                </button>
              ))}
              {!readOnly && (
                <button type="button" onClick={() => onRemove(name)} className="opacity-60 hover:opacity-100" aria-label={`Remove ${name}`}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomAddRow({ onAdd, testIdPrefix }) {
  const [name, setName] = useState('');
  const [asEssential, setAsEssential] = useState(false);
  const submit = () => {
    const n = name.trim();
    if (!n) return;
    onAdd(n, asEssential);
    setName('');
  };
  return (
    <div className="rounded-lg border border-dashed bg-muted/40 p-2.5 flex items-center gap-2">
      <Input
        value={name} onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        placeholder="Add custom..." className="h-7 text-xs bg-card flex-1"
        data-testid={`${testIdPrefix}-input`}
      />
      <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setAsEssential((v) => !v)} data-testid={`${testIdPrefix}-toggle`}>
        {asEssential ? 'Essential' : 'Standard'}
      </Button>
      <Button type="button" size="sm" className="h-7" onClick={submit} disabled={!name.trim()} data-testid={`${testIdPrefix}-add`}>
        <Plus className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
